/**
 * QualityInspectionCapture · iter303ak-fix34 — Job Completed · T10 · Action 10
 * ────────────────────────────────────────────────────────────────────────
 * PER-COMPONENT-AREA re-inspection.
 *
 * For every accepted child × component_type (Ridge / Barge / Valley / Sheet /
 * Flashing / Spouting / Fastening / Tile / Aquaseal / …) the QA inspector
 * rates the work with one of {Excellent, Good, Needs Fix, Poor}, jots notes,
 * and (optionally) attaches area-specific photos as evidence. Each area
 * shows the WIP tick state + contractor notes read-only alongside so the
 * inspector has the original crew's context.
 *
 * Overall rating is auto-suggested as the worst rating across all rated
 * areas; the inspector can override manually.
 *
 * Persists parent.quality_inspection = {
 *   inspector_name, inspected_at, rating, rating_manual, notes,
 *   photo_attachment_ids[],
 *   per_area_ratings: {[child_id]:{[component_type]:{rating,notes,photo_ids[]}}}
 * }.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { ShieldCheck, AlertTriangle, ClipboardCheck, Camera, X, Star, ChevronDown, ChevronRight, Sparkles } from 'lucide-react';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`, withCredentials: true });

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const attachmentSrc = (id) => {
  const base = `${BACKEND}/api/attachments/${id}/download`;
  return base;
};

const RATINGS = [
  { key: 'Excellent',   rank: 4, active: 'bg-emerald-600 text-white border-emerald-700', inactive: 'bg-white text-emerald-700 border-emerald-300 hover:bg-emerald-50' },
  { key: 'Good',        rank: 3, active: 'bg-sky-600 text-white border-sky-700',         inactive: 'bg-white text-sky-700 border-sky-300 hover:bg-sky-50' },
  { key: 'Needs Fix',   rank: 2, active: 'bg-amber-600 text-white border-amber-700',     inactive: 'bg-white text-amber-700 border-amber-300 hover:bg-amber-50' },
  { key: 'Poor',        rank: 1, active: 'bg-rose-600 text-white border-rose-700',       inactive: 'bg-white text-rose-700 border-rose-300 hover:bg-rose-50' },
];
const RATING_RANK = RATINGS.reduce((m, r) => { m[r.key] = r.rank; return m; }, {});

// Compact rating pill row — reused for area + overall.
function RatingPills({ value, onChange, testIdPrefix }) {
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {RATINGS.map(r => {
        const active = value === r.key;
        return (
          <button key={r.key} type="button" onClick={() => onChange(r.key)}
            data-testid={`${testIdPrefix}-${r.key.replace(/\s/g, '')}`}
            className={`px-3 py-1 rounded-full text-[11px] font-semibold border transition-all ${active ? r.active : r.inactive}`}>
            {r.key}
          </button>
        );
      })}
    </div>
  );
}

// One component-area accordion row.
function AreaRow({ childId, componentType, wipTicks, componentLines, areaState, onChange, onUploadPhoto, onRemovePhoto, uploading }) {
  const [open, setOpen] = useState(false);
  const rating = areaState.rating || '';
  const notes = areaState.notes || '';
  const photoIds = areaState.photo_ids || [];

  // WIP context — count ticked vs total for this component_type
  const wipDoneCount = componentLines.filter(l => (wipTicks?.[l.line_id]?.done)).length;
  const wipTotal = componentLines.length;
  const wipNotes = componentLines
    .map(l => wipTicks?.[l.line_id]?.notes)
    .filter(n => n && n.trim())
    .join(' · ');

  const ratingBadge = rating
    ? (RATINGS.find(r => r.key === rating) || {})
    : { active: 'bg-slate-100 text-slate-500 border-slate-300' };

  return (
    <div className={`border rounded-md overflow-hidden ${rating === 'Poor' ? 'border-rose-300 bg-rose-50/40' :
      rating === 'Needs Fix' ? 'border-amber-300 bg-amber-50/40' :
      rating === 'Good' ? 'border-sky-200 bg-sky-50/30' :
      rating === 'Excellent' ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-200 bg-white'}`}
      data-testid={`wf-qi-area-${childId}-${componentType}`}>
      <button type="button" onClick={() => setOpen(o => !o)}
        data-testid={`wf-qi-area-toggle-${childId}-${componentType}`}
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-slate-50">
        {open ? <ChevronDown className="w-3.5 h-3.5 text-slate-500" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-500" />}
        <span className="text-sm font-semibold text-slate-800">{componentType}</span>
        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${ratingBadge.active}`}>
          {rating || 'Not rated'}
        </span>
        <span className={`ml-auto text-[10px] font-medium ${wipDoneCount === wipTotal && wipTotal > 0 ? 'text-emerald-700' : 'text-slate-500'}`}>
          WIP {wipDoneCount}/{wipTotal} ticked
        </span>
      </button>
      {open && (
        <div className="px-3 pb-3 pt-1 border-t border-slate-200 space-y-2 bg-white/60">
          {/* WIP context — read-only from Work In Progress */}
          <div className="p-2 rounded bg-slate-50 border border-slate-200 text-[11px] text-slate-600 space-y-0.5">
            <div className="font-semibold uppercase tracking-widest text-slate-500">WIP ticks — from Work In Progress</div>
            {componentLines.map(l => {
              const t = wipTicks?.[l.line_id] || {};
              return (
                <div key={l.line_id} className="flex items-center gap-2">
                  <span className={t.done ? 'text-emerald-700' : 'text-slate-500'}>
                    {t.done ? '✓' : '○'}
                  </span>
                  <span>{l.type_name || l.name || 'Line'}</span>
                  {l.quantity ? <span className="text-slate-400">· {l.quantity} {l.length ? `× ${l.length}m` : ''}</span> : null}
                  {t.notes ? <span className="italic text-slate-500 truncate">— {t.notes}</span> : null}
                </div>
              );
            })}
            {!wipNotes && componentLines.every(l => !wipTicks?.[l.line_id]?.notes) && (
              <div className="text-slate-400 italic">No crew notes on these lines.</div>
            )}
          </div>

          {/* Rating pills */}
          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">
              <Star className="w-3 h-3 inline mr-1 -mt-0.5" /> Rating
            </label>
            <RatingPills value={rating}
              onChange={(v) => onChange({ ...areaState, rating: v })}
              testIdPrefix={`wf-qi-area-rating-${childId}-${componentType}`} />
          </div>

          {/* Area notes */}
          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Area notes</label>
            <textarea value={notes} onChange={(e) => onChange({ ...areaState, notes: e.target.value })}
              placeholder="e.g. corner ridge cap needs re-seat · lap flashings all crisp · etc."
              data-testid={`wf-qi-area-notes-${childId}-${componentType}`}
              className="w-full px-2 py-1 text-xs border border-slate-300 rounded" rows={2} />
          </div>

          {/* Area photos */}
          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Area photos</label>
            <div className="flex items-start gap-1.5 flex-wrap">
              {photoIds.map(pid => (
                <div key={pid} className="relative w-14 h-14 border border-slate-300 rounded overflow-hidden bg-white group">
                  <img src={attachmentSrc(pid)} alt="Area photo"
                    data-testid={`wf-qi-area-photo-${childId}-${componentType}-${pid}`}
                    className="w-full h-full object-cover" />
                  <button onClick={() => onRemovePhoto(pid)}
                    data-testid={`wf-qi-area-photo-remove-${childId}-${componentType}-${pid}`}
                    className="absolute top-0.5 right-0.5 bg-rose-600 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100">
                    <X size={9} />
                  </button>
                </div>
              ))}
              <label className="w-14 h-14 border-2 border-dashed border-slate-300 rounded flex items-center justify-center cursor-pointer hover:bg-slate-50">
                <input type="file" accept="image/*" onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) onUploadPhoto(f);
                  e.target.value = '';
                }} disabled={uploading} className="hidden"
                  data-testid={`wf-qi-area-photo-upload-${childId}-${componentType}`} />
                <Camera size={16} className={uploading ? 'text-slate-400 animate-pulse' : 'text-slate-500'} />
              </label>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function QualityInspectionCapture({ action, capturedData, onNext, onBack, onCancel, canBack, value, onChange }) {
  const params = action.params || {};
  const parentId = capturedData?.appraisal_id;
  const attachmentKind = params.attachment_kind || 'After Photos';

  const parent = useMemo(() => (
    capturedData?.appraisal_record || capturedData?.appraisal || {}
  ), [capturedData]);

  const prior = useMemo(() => parent.quality_inspection || {}, [parent]);

  // Top-level QI state.
  const [inspector, setInspector] = useState(value?.inspector_name || prior.inspector_name || '');
  const [inspectedDate, setInspectedDate] = useState(() => {
    const iso = value?.inspected_at || prior.inspected_at;
    return iso ? iso.slice(0, 10) : new Date().toISOString().slice(0, 10);
  });
  const [overallRating, setOverallRating] = useState(value?.rating || prior.rating || '');
  const [overallRatingManual, setOverallRatingManual] = useState(!!(value?.rating_manual ?? prior.rating_manual));
  const [notes, setNotes] = useState(value?.notes || prior.notes || '');
  const [photoIds, setPhotoIds] = useState(value?.photo_attachment_ids || prior.photo_attachment_ids || []);
  const [perAreaRatings, setPerAreaRatings] = useState(value?.per_area_ratings || prior.per_area_ratings || {});
  // iter303ak-fix35 · Per-child QI sign-off dates (Q3=b) — one date + optional
  // notes per accepted child, feeding the Certificate of Completion at Paid T20.
  const [perChildSignoff, setPerChildSignoff] = useState(value?.per_child_signoff || prior.per_child_signoff || {});

  const patchChildSignoff = (child_id, patch) => {
    setPerChildSignoff(prev => ({
      ...prev,
      [child_id]: { ...(prev[child_id] || {}), ...patch },
    }));
  };

  // Data loading — per-child component-lines (source of the per-area rows).
  const [childBreakdowns, setChildBreakdowns] = useState({});   // {child_id: {groups:[{component_type, lines:[]}], appraisal_no, quote_type_ref, variant_label}}
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState('');
  const [uploadingKey, setUploadingKey] = useState(null);

  const acceptedChildren = useMemo(() => {
    const decisions = parent.child_decisions || {};
    return Object.entries(decisions)
      .filter(([, d]) => d?.decision === 'accepted')
      .map(([child_id, d]) => ({ child_id, ...d }));
  }, [parent]);

  const wipTicksByChild = useMemo(
    () => parent.work_execution?.per_child_breakdown_ticks || {},
    [parent],
  );

  const load = useCallback(async () => {
    if (!acceptedChildren.length) { setLoading(false); return; }
    setLoading(true);
    const map = {};
    for (const c of acceptedChildren) {
      try {
        const { data } = await api.get(`/appraisals/${c.child_id}/component-lines-summary`);
        map[c.child_id] = data;
      } catch { /* silent */ }
    }
    setChildBreakdowns(map);
    setLoading(false);
  }, [acceptedChildren]);

  useEffect(() => { load(); }, [load]);

  const loadCurrentUser = useCallback(async () => {
    if (inspector) return;
    try {
      const { data } = await api.get('/auth/me');
      const name = data?.name || data?.username || data?.email || '';
      if (name) setInspector(name);
    } catch { /* silent */ }
  }, [inspector]);
  useEffect(() => { loadCurrentUser(); }, [loadCurrentUser]);

  // Auto-compute overall rating from per-area ratings — worst wins.
  const autoOverall = useMemo(() => {
    let worst = null;
    for (const child_id of Object.keys(perAreaRatings)) {
      for (const ct of Object.keys(perAreaRatings[child_id] || {})) {
        const r = perAreaRatings[child_id][ct]?.rating;
        if (!r) continue;
        if (!worst || RATING_RANK[r] < RATING_RANK[worst]) worst = r;
      }
    }
    return worst;
  }, [perAreaRatings]);

  // If not manually set, sync overall to auto whenever it changes.
  useEffect(() => {
    if (!overallRatingManual && autoOverall && autoOverall !== overallRating) {
      setOverallRating(autoOverall);
    }
  }, [autoOverall, overallRatingManual, overallRating]);

  // Broadcast state upstream.
  useEffect(() => {
    if (onChange) {
      const inspected_at = inspectedDate ? new Date(`${inspectedDate}T09:00:00`).toISOString() : null;
      onChange({
        inspector_name: inspector,
        inspected_at,
        rating: overallRating,
        rating_manual: overallRatingManual,
        notes,
        photo_attachment_ids: photoIds,
        per_area_ratings: perAreaRatings,
        per_child_signoff: perChildSignoff,
      });
    }
  }, [inspector, inspectedDate, overallRating, overallRatingManual, notes, photoIds, perAreaRatings, perChildSignoff]);  // eslint-disable-line react-hooks/exhaustive-deps

  const patchArea = (child_id, component_type, patch) => {
    setPerAreaRatings(prev => ({
      ...prev,
      [child_id]: {
        ...(prev[child_id] || {}),
        [component_type]: { ...((prev[child_id] || {})[component_type] || {}), ...patch },
      },
    }));
  };

  const uploadPhoto = async (file, scope) => {
    if (!file) return;
    // scope: { kind: 'overall' } OR { kind: 'area', child_id, component_type }
    const key = scope.kind === 'overall'
      ? 'overall'
      : `area:${scope.child_id}:${scope.component_type}`;
    setUploadingKey(key);
    setSaveErr('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('scope', 'appraisal');
      fd.append('scope_id', parentId);
      fd.append('kind', attachmentKind);
      fd.append('notes', scope.kind === 'area'
        ? `QI area photo · ${scope.component_type}`
        : 'QI photo (overall)');
      const { data } = await api.post('/attachments', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (scope.kind === 'overall') {
        setPhotoIds(prev => [...prev, data._id]);
      } else {
        const curArea = perAreaRatings[scope.child_id]?.[scope.component_type] || {};
        const curPhotos = curArea.photo_ids || [];
        patchArea(scope.child_id, scope.component_type, { photo_ids: [...curPhotos, data._id] });
      }
    } catch (e) {
      setSaveErr(e?.response?.data?.detail || e?.message || 'Photo upload failed');
    } finally {
      setUploadingKey(null);
    }
  };

  const removeOverallPhoto = (id) => setPhotoIds(prev => prev.filter(x => x !== id));
  const removeAreaPhoto = (child_id, component_type, id) => {
    const cur = perAreaRatings[child_id]?.[component_type] || {};
    patchArea(child_id, component_type, { photo_ids: (cur.photo_ids || []).filter(x => x !== id) });
  };

  const areaRatedCount = useMemo(() => {
    let done = 0, total = 0;
    for (const c of acceptedChildren) {
      const br = childBreakdowns[c.child_id];
      if (!br) continue;
      for (const g of br.groups || []) {
        total += 1;
        if (perAreaRatings[c.child_id]?.[g.component_type]?.rating) done += 1;
      }
    }
    return { done, total };
  }, [acceptedChildren, childBreakdowns, perAreaRatings]);

  const persistAndContinue = async () => {
    if (!inspector.trim()) { setSaveErr('Inspector name required.'); return; }
    if (!overallRating) { setSaveErr('Pick or auto-suggest an overall rating.'); return; }
    if (areaRatedCount.total > 0 && areaRatedCount.done === 0) {
      setSaveErr('Rate at least one component area before continuing.');
      return;
    }
    setSaving(true);
    setSaveErr('');
    try {
      const payload = {
        inspector_name: inspector,
        inspected_at: new Date(`${inspectedDate}T09:00:00`).toISOString(),
        rating: overallRating,
        rating_manual: overallRatingManual,
        notes,
        photo_attachment_ids: photoIds,
        per_area_ratings: perAreaRatings,
        per_child_signoff: perChildSignoff,
      };
      if (parentId) {
        await api.patch(`/appraisals/${parentId}`, { quality_inspection: payload });
      }
      onNext();
    } catch (e) {
      setSaveErr(e?.response?.data?.detail || e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const canContinue = !!inspector.trim() && !!inspectedDate && !!overallRating &&
    (areaRatedCount.total === 0 || areaRatedCount.done > 0);

  return (
    <div className="space-y-4" data-testid="wf-qi-capture">
      <div className="rounded-xl border border-indigo-200 bg-indigo-50/60 p-4">
        <div className="flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 mt-0.5 shrink-0 text-indigo-700" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-indigo-900">Quality Inspection · Per-Area Grading</div>
            <div className="text-sm mt-1 text-indigo-800">
              {params.prompt || 'Physically inspect each component area and grade the workmanship. The overall rating auto-computes from the worst area score and drives the contractor payment split at T20.'}
            </div>
          </div>
        </div>
      </div>

      {/* Inspector + date */}
      <div className="p-4 border border-slate-200 rounded-lg bg-white">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Inspector <span className="text-rose-500">*</span></label>
            <input type="text" value={inspector} onChange={(e) => setInspector(e.target.value)}
              placeholder="Inspector name"
              data-testid="wf-qi-inspector"
              className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded" />
          </div>
          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Inspection date <span className="text-rose-500">*</span></label>
            <input type="date" value={inspectedDate} onChange={(e) => setInspectedDate(e.target.value)}
              data-testid="wf-qi-date"
              className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded font-mono" />
          </div>
        </div>
      </div>

      {/* Per-child · per-area rating accordions */}
      {loading && <div className="p-4 text-sm text-slate-500 italic">Loading component breakdown…</div>}

      {!loading && acceptedChildren.map(c => {
        const br = childBreakdowns[c.child_id] || { groups: [] };
        const wipTicks = wipTicksByChild[c.child_id] || {};
        const childSo = perChildSignoff[c.child_id] || {};
        // Auto-suggest QI sign-off date = today (Trevor Q3=b · per-child dates).
        const defaultSignoff = new Date().toISOString().slice(0, 10);
        const signoffDate = childSo.qi_signoff_date || defaultSignoff;
        return (
          <div key={c.child_id} className="p-3 border border-slate-200 rounded-lg bg-slate-50/40"
            data-testid={`wf-qi-child-${c.child_id}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-semibold text-slate-800">{c.quote_type_ref}</span>
              {c.variant_label && <span className="text-xs text-slate-600">· {c.variant_label}</span>}
              {c.appraisal_no && <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-800 bg-indigo-100 border border-indigo-300 rounded px-1.5 py-0.5">A{c.appraisal_no}</span>}
              <span className="ml-auto text-[10px] font-medium text-slate-500">
                {(br.groups || []).length} area{(br.groups || []).length === 1 ? '' : 's'}
              </span>
            </div>
            {/* Per-child QI sign-off (iter303ak-fix35) — feeds cert PDF. */}
            <div className="mb-2 p-2 border border-emerald-200 bg-emerald-50/40 rounded flex items-center gap-3 flex-wrap"
              data-testid={`wf-qi-child-signoff-${c.child_id}`}>
              <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-800">QI sign-off</span>
              <label className="text-[10px] text-slate-500 font-semibold uppercase tracking-widest">Date</label>
              <input type="date" value={signoffDate}
                onChange={(e) => patchChildSignoff(c.child_id, { qi_signoff_date: e.target.value })}
                data-testid={`wf-qi-child-signoff-date-${c.child_id}`}
                className="px-2 py-0.5 text-xs border border-slate-300 rounded font-mono" />
              <input type="text" value={childSo.notes || ''}
                onChange={(e) => patchChildSignoff(c.child_id, { notes: e.target.value })}
                placeholder="Sign-off notes (optional)…"
                data-testid={`wf-qi-child-signoff-notes-${c.child_id}`}
                className="flex-1 min-w-[220px] px-2 py-0.5 text-xs border border-slate-300 rounded" />
            </div>
            {(br.groups || []).length === 0 ? (
              <div className="text-xs text-slate-500 italic px-2 py-1">No component lines on this child — nothing to inspect.</div>
            ) : (
              <div className="space-y-1.5">
                {(br.groups || []).map(g => {
                  const areaKey = `${c.child_id}|${g.component_type}`;
                  const areaState = perAreaRatings[c.child_id]?.[g.component_type] || {};
                  return (
                    <AreaRow
                      key={areaKey}
                      childId={c.child_id}
                      componentType={g.component_type}
                      wipTicks={wipTicks}
                      componentLines={g.lines || []}
                      areaState={areaState}
                      onChange={(patch) => patchArea(c.child_id, g.component_type, patch)}
                      onUploadPhoto={(f) => uploadPhoto(f, { kind: 'area', child_id: c.child_id, component_type: g.component_type })}
                      onRemovePhoto={(pid) => removeAreaPhoto(c.child_id, g.component_type, pid)}
                      uploading={uploadingKey === `area:${c.child_id}:${g.component_type}`}
                    />
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {/* Overall rating banner */}
      <div className="p-4 border-2 border-indigo-300 rounded-lg bg-indigo-50/40">
        <div className="flex items-start gap-3">
          <Sparkles className="w-4 h-4 mt-1 text-indigo-700" />
          <div className="flex-1 space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold uppercase tracking-widest text-indigo-800">Overall Rating</span>
              <span className="text-[10px] text-slate-500">
                · {areaRatedCount.done}/{areaRatedCount.total} areas rated
              </span>
              {autoOverall && !overallRatingManual && (
                <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full border border-emerald-300">
                  Auto-suggested (worst area = {autoOverall})
                </span>
              )}
              {overallRatingManual && (
                <button type="button" onClick={() => { setOverallRatingManual(false); if (autoOverall) setOverallRating(autoOverall); }}
                  data-testid="wf-qi-overall-reset-auto"
                  className="text-[10px] font-semibold text-indigo-700 underline hover:text-indigo-900">
                  Reset to auto
                </button>
              )}
            </div>
            <RatingPills value={overallRating}
              onChange={(v) => { setOverallRating(v); setOverallRatingManual(true); }}
              testIdPrefix="wf-qi-overall-rating" />
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Overall notes</label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                placeholder="Global observations across the whole job — e.g. crew site tidiness, client feedback, weather delays."
                data-testid="wf-qi-notes"
                className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded" rows={2} />
            </div>
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Overall photos</label>
              <div className="flex items-start gap-2 flex-wrap">
                {photoIds.map(pid => (
                  <div key={pid} className="relative w-16 h-16 border border-slate-300 rounded overflow-hidden bg-white group">
                    <img src={attachmentSrc(pid)} alt="QI photo"
                      data-testid={`wf-qi-photo-${pid}`}
                      className="w-full h-full object-cover" />
                    <button onClick={() => removeOverallPhoto(pid)}
                      data-testid={`wf-qi-photo-remove-${pid}`}
                      className="absolute top-0.5 right-0.5 bg-rose-600 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100">
                      <X size={10} />
                    </button>
                  </div>
                ))}
                <label className="w-16 h-16 border-2 border-dashed border-slate-300 rounded flex items-center justify-center cursor-pointer hover:bg-slate-50">
                  <input type="file" accept="image/*" onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) uploadPhoto(f, { kind: 'overall' });
                    e.target.value = '';
                  }} disabled={uploadingKey === 'overall'} className="hidden"
                    data-testid="wf-qi-photo-upload" />
                  <Camera size={18} className={uploadingKey === 'overall' ? 'text-slate-400 animate-pulse' : 'text-slate-500'} />
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      {saveErr && (
        <div className="flex items-start gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-3">
          <AlertTriangle className="w-4 h-4 mt-0.5" /> <span>{saveErr}</span>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 pt-2">
        {canBack ? <button onClick={onBack} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100">← Back</button> : <span />}
        <div className="flex gap-2">
          {onCancel && <button onClick={onCancel} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100">Cancel</button>}
          <button onClick={persistAndContinue} disabled={!canContinue || saving}
            data-testid="wf-qi-continue"
            className="px-4 py-1.5 rounded-md text-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-2">
            <ClipboardCheck className="w-4 h-4" /> {saving ? 'Saving…' : 'Save & Continue'}
          </button>
        </div>
      </div>
    </div>
  );
}
